/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name anova1
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/anova1}
*/
import anova1 from './../anova1';
setReadOnly( ns, 'anova1', anova1 );

/**
* @name array
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/array}
*/
import array from './../array';
setReadOnly( ns, 'array', array );

/**
* @name bartlettTest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/bartlett-test}
*/
import bartlettTest from './../bartlett-test';
setReadOnly( ns, 'bartlettTest', bartlettTest );

/**
* @name base
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base}
*/
import base from './../base';
setReadOnly( ns, 'base', base );

/**
* @name binomialTest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/binomial-test}
*/
import binomialTest from './../binomial-test';
setReadOnly( ns, 'binomialTest', binomialTest );

/**
* @name chi2gof
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/chi2gof}
*/
import chi2gof from './../chi2gof';
setReadOnly( ns, 'chi2gof', chi2gof );

/**
* @name chi2test
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/chi2test}
*/
import chi2test from './../chi2test';
setReadOnly( ns, 'chi2test', chi2test );

/**
* @name cumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/cumax}
*/
import cumax from './../cumax';
setReadOnly( ns, 'cumax', cumax );

/**
* @name cumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/cumin}
*/
import cumin from './../cumin';
setReadOnly( ns, 'cumin', cumin );

/**
* @name flignerTest
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/fligner-test}
*/
import flignerTest from './../fligner-test';
setReadOnly( ns, 'flignerTest', flignerTest );

/**
* @name incr
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/incr}
*/
import incr from './../incr';
setReadOnly( ns, 'incr', incr );

/**
* @name iterators
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/iter}
*/
import iterators from './../iter';
setReadOnly( ns, 'iterators', iterators );

/**
* @name kde2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/kde2d}
*/
import kde2d from './../kde2d';
setReadOnly( ns, 'kde2d', kde2d );

/**
* @name kruskalTest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/kruskal-test}
*/
import kruskalTest from './../kruskal-test';
setReadOnly( ns, 'kruskalTest', kruskalTest );

/**
* @name kstest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/kstest}
*/
import kstest from './../kstest';
setReadOnly( ns, 'kstest', kstest );

/**
* @name leveneTest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/levene-test}
*/
import leveneTest from './../levene-test';
setReadOnly( ns, 'leveneTest', leveneTest );

/**
* @name lowess
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/lowess}
*/
import lowess from './../lowess';
setReadOnly( ns, 'lowess', lowess );

/**
* @name max
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/max}
*/
import max from './../max';
setReadOnly( ns, 'max', max );

/**
* @name maxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/max-by}
*/
import maxBy from './../max-by';
setReadOnly( ns, 'maxBy', maxBy );

/**
* @name maxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/maxabs}
*/
import maxabs from './../maxabs';
setReadOnly( ns, 'maxabs', maxabs );

/**
* @name maxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/maxsorted}
*/
import maxsorted from './../maxsorted';
setReadOnly( ns, 'maxsorted', maxsorted );

/**
* @name mean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/mean}
*/
import mean from './../mean';
setReadOnly( ns, 'mean', mean );

/**
* @name meankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/meankbn}
*/
import meankbn from './../meankbn';
setReadOnly( ns, 'meankbn', meankbn );

/**
* @name meankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/meankbn2}
*/
import meankbn2 from './../meankbn2';
setReadOnly( ns, 'meankbn2', meankbn2 );

/**
* @name meanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/meanors}
*/
import meanors from './../meanors';
setReadOnly( ns, 'meanors', meanors );

/**
* @name meanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/meanpn}
*/
import meanpn from './../meanpn';
setReadOnly( ns, 'meanpn', meanpn );

/**
* @name meanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/meanpw}
*/
import meanpw from './../meanpw';
setReadOnly( ns, 'meanpw', meanpw );

/**
* @name meanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/meanwd}
*/
import meanwd from './../meanwd';
setReadOnly( ns, 'meanwd', meanwd );

/**
* @name mediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/mediansorted}
*/
import mediansorted from './../mediansorted';
setReadOnly( ns, 'mediansorted', mediansorted );

/**
* @name midrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/midrange}
*/
import midrange from './../midrange';
setReadOnly( ns, 'midrange', midrange );

/**
* @name midrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/midrange-by}
*/
import midrangeBy from './../midrange-by';
setReadOnly( ns, 'midrangeBy', midrangeBy );

/**
* @name min
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/min}
*/
import min from './../min';
setReadOnly( ns, 'min', min );

/**
* @name minBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/min-by}
*/
import minBy from './../min-by';
setReadOnly( ns, 'minBy', minBy );

/**
* @name minabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/minabs}
*/
import minabs from './../minabs';
setReadOnly( ns, 'minabs', minabs );

/**
* @name minsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/minsorted}
*/
import minsorted from './../minsorted';
setReadOnly( ns, 'minsorted', minsorted );

/**
* @name nanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmax}
*/
import nanmax from './../nanmax';
setReadOnly( ns, 'nanmax', nanmax );

/**
* @name nanmaxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmax-by}
*/
import nanmaxBy from './../nanmax-by';
setReadOnly( ns, 'nanmaxBy', nanmaxBy );

/**
* @name nanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmaxabs}
*/
import nanmaxabs from './../nanmaxabs';
setReadOnly( ns, 'nanmaxabs', nanmaxabs );

/**
* @name nanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmean}
*/
import nanmean from './../nanmean';
setReadOnly( ns, 'nanmean', nanmean );

/**
* @name nanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmeanors}
*/
import nanmeanors from './../nanmeanors';
setReadOnly( ns, 'nanmeanors', nanmeanors );

/**
* @name nanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmeanpn}
*/
import nanmeanpn from './../nanmeanpn';
setReadOnly( ns, 'nanmeanpn', nanmeanpn );

/**
* @name nanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmeanwd}
*/
import nanmeanwd from './../nanmeanwd';
setReadOnly( ns, 'nanmeanwd', nanmeanwd );

/**
* @name nanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmidrange}
*/
import nanmidrange from './../nanmidrange';
setReadOnly( ns, 'nanmidrange', nanmidrange );

/**
* @name nanmidrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmidrange-by}
*/
import nanmidrangeBy from './../nanmidrange-by';
setReadOnly( ns, 'nanmidrangeBy', nanmidrangeBy );

/**
* @name nanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmin}
*/
import nanmin from './../nanmin';
setReadOnly( ns, 'nanmin', nanmin );

/**
* @name nanminBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanmin-by}
*/
import nanminBy from './../nanmin-by';
setReadOnly( ns, 'nanminBy', nanminBy );

/**
* @name nanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanminabs}
*/
import nanminabs from './../nanminabs';
setReadOnly( ns, 'nanminabs', nanminabs );

/**
* @name nanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanrange}
*/
import nanrange from './../nanrange';
setReadOnly( ns, 'nanrange', nanrange );

/**
* @name nanrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/nanrange-by}
*/
import nanrangeBy from './../nanrange-by';
setReadOnly( ns, 'nanrangeBy', nanrangeBy );

/**
* @name padjust
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/padjust}
*/
import padjust from './../padjust';
setReadOnly( ns, 'padjust', padjust );

/**
* @name pcorrtest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/pcorrtest}
*/
import pcorrtest from './../pcorrtest';
setReadOnly( ns, 'pcorrtest', pcorrtest );

/**
* @name range
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/range}
*/
import range from './../range';
setReadOnly( ns, 'range', range );

/**
* @name rangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/range-by}
*/
import rangeBy from './../range-by';
setReadOnly( ns, 'rangeBy', rangeBy );

/**
* @name rangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/rangeabs}
*/
import rangeabs from './../rangeabs';
setReadOnly( ns, 'rangeabs', rangeabs );

/**
* @name ranks
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/ranks}
*/
import ranks from './../ranks';
setReadOnly( ns, 'ranks', ranks );

/**
* @name strided
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/strided}
*/
import strided from './../strided';
setReadOnly( ns, 'strided', strided );

/**
* @name ttest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/ttest}
*/
import ttest from './../ttest';
setReadOnly( ns, 'ttest', ttest );

/**
* @name ttest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/ttest2}
*/
import ttest2 from './../ttest2';
setReadOnly( ns, 'ttest2', ttest2 );

/**
* @name vartest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/vartest}
*/
import vartest from './../vartest';
setReadOnly( ns, 'vartest', vartest );

/**
* @name wilcoxon
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/wilcoxon}
*/
import wilcoxon from './../wilcoxon';
setReadOnly( ns, 'wilcoxon', wilcoxon );

/**
* @name ztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/ztest}
*/
import ztest from './../ztest';
setReadOnly( ns, 'ztest', ztest );

/**
* @name ztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/ztest2}
*/
import ztest2 from './../ztest2';
setReadOnly( ns, 'ztest2', ztest2 );


// EXPORTS //

export default ns;
